/**
 * ============================================================================
 * ANALYTICS PROVIDER
 * ============================================================================
 * 
 * Provides analytics tracking for the app
 */

import React, { createContext, useContext, useCallback, useEffect } from 'react';
import { useAuthStore } from '../store';
import { useAnalytics } from '../hooks/useAnalytics';

const AnalyticsContext = createContext(null);

/**
 * Analytics Provider Component
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {Object} props.config - Analytics configuration
 * @returns {JSX.Element}
 */
export const AnalyticsProvider = ({ children, config = {} }) => {
  const { user, isAuthenticated } = useAuthStore();
  const analyticsHook = useAnalytics();

  // Initialize analytics
  useEffect(() => {
    // Initialize Google Analytics if available
    if (window.gtag && config.googleAnalyticsId) {
      window.gtag('config', config.googleAnalyticsId, {
        page_path: window.location.pathname,
      });
    }

    // Initialize Mixpanel if available
    if (window.mixpanel && config.mixpanelToken) {
      window.mixpanel.init(config.mixpanelToken);
    }

    // Initialize PostHog if available
    if (window.posthog && config.posthogKey) {
      window.posthog.init(config.posthogKey, {
        api_host: config.posthogHost || 'https://app.posthog.com',
      });
    }
  }, [config]);

  // Identify user when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Google Analytics
      if (window.gtag) {
        window.gtag('set', { user_id: user.id });
      }

      // Mixpanel
      if (window.mixpanel) {
        window.mixpanel.identify(user.id);
        window.mixpanel.people.set({
          email: user.email,
          phone: user.phone,
          name: user.name,
        });
      }

      // PostHog
      if (window.posthog) {
        window.posthog.identify(user.id, {
          email: user.email,
          phone: user.phone,
          name: user.name,
        });
      }
    }
  }, [isAuthenticated, user]);

  // Track page view
  const trackPageView = useCallback(
    (path, title) => {
      const pageData = {
        path: path || window.location.pathname,
        title: title || document.title,
      };

      // Google Analytics
      if (window.gtag) {
        window.gtag('config', config.googleAnalyticsId, pageData);
      }

      // Mixpanel
      if (window.mixpanel) {
        window.mixpanel.track('Page View', pageData);
      }

      // PostHog
      if (window.posthog) {
        window.posthog.capture('$pageview', pageData);
      }
    },
    [config]
  );

  // Track event
  const trackEvent = useCallback(
    (eventName, properties = {}) => {
      const eventData = {
        ...properties,
        timestamp: new Date().toISOString(),
        userId: user?.id,
      };

      // Google Analytics
      if (window.gtag) {
        window.gtag('event', eventName, eventData);
      }

      // Mixpanel
      if (window.mixpanel) {
        window.mixpanel.track(eventName, eventData);
      }

      // PostHog
      if (window.posthog) {
        window.posthog.capture(eventName, eventData);
      }

      // Custom analytics hook
      analyticsHook.loadCustomAnalytics('track-event', { eventName, ...eventData });
    },
    [user, analyticsHook]
  );

  // Track transaction
  const trackTransaction = useCallback(
    (transaction) => {
      trackEvent('Transaction Created', {
        transactionId: transaction.id,
        amount: transaction.amount,
        currency: transaction.currency,
        type: transaction.type,
      });
    },
    [trackEvent]
  );

  // Track payment
  const trackPayment = useCallback(
    (payment) => {
      trackEvent('Payment Processed', {
        paymentId: payment.id,
        amount: payment.amount,
        method: payment.method,
        status: payment.status,
      });
    },
    [trackEvent]
  );

  // Track error
  const trackError = useCallback(
    (error, context = {}) => {
      trackEvent('Error Occurred', {
        error: error.message,
        stack: error.stack,
        ...context,
      });
    },
    [trackEvent]
  );

  const value = {
    trackPageView,
    trackEvent,
    trackTransaction,
    trackPayment,
    trackError,
    // Analytics hook methods
    ...analyticsHook,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * Use analytics context
 * @returns {Object} Analytics context value
 */
export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    // Fallback to basic implementation
    return {
      trackPageView: () => {},
      trackEvent: () => {},
      trackTransaction: () => {},
      trackPayment: () => {},
      trackError: () => {},
    };
  }
  return context;
};

export default AnalyticsProvider;
